import React, { useState, useCallback, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Slider,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { getCroppedImg } from '../utils/cropImage';

const ASPECT_OPTIONS = [
  { label: '4:3', hint: 'Grid & detail photo', value: 4 / 3 },
  { label: '3:4', hint: 'Home page hero', value: 3 / 4 },
  { label: '3:2', hint: 'Featured card', value: 3 / 2 },
];

const PhotoCropDialog = ({ open, imageSrc, onConfirm, onCancel }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [aspect, setAspect] = useState(ASPECT_OPTIONS[0].value);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [saving, setSaving] = useState(false);
  const [setAsHero, setSetAsHero] = useState(false);

  useEffect(() => {
    setSetAsHero(false);
  }, [imageSrc]);

  const handleCropComplete = useCallback((_croppedArea, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleAspectChange = (_e, value) => {
    if (value !== null) setAspect(value);
  };

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    setSaving(true);
    try {
      const blob = await getCroppedImg(imageSrc, croppedAreaPixels);
      await onConfirm(blob, setAsHero);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Crop Photo</DialogTitle>
      <DialogContent>
        <Box sx={{ position: 'relative', width: '100%', height: 340, bgcolor: 'black' }}>
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={handleCropComplete}
            />
          )}
        </Box>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <ToggleButtonGroup
            value={aspect}
            exclusive
            size="small"
            onChange={handleAspectChange}
          >
            {ASPECT_OPTIONS.map((opt) => (
              <ToggleButton key={opt.label} value={opt.value} sx={{ flexDirection: 'column', lineHeight: 1.2, py: 0.75 }}>
                <Typography variant="body2" component="span">
                  {opt.label}
                </Typography>
                <Typography variant="caption" component="span" color="text.secondary">
                  {opt.hint}
                </Typography>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ mt: 2, px: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Zoom
          </Typography>
          <Slider
            value={zoom}
            min={1}
            max={3}
            step={0.05}
            onChange={(_e, value) => setZoom(value)}
          />
        </Box>

        <Box sx={{ mt: 1, px: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={setAsHero}
                onChange={(e) => setSetAsHero(e.target.checked)}
              />
            }
            label="Set as site hero image (ktmranch.com homepage)"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleConfirm} disabled={!croppedAreaPixels || saving}>
          {saving ? 'Saving…' : 'Save & Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PhotoCropDialog;
